/** The fields of a workflow action that say whether it still waits for an answer; both the sidebar list and the detail
 * panel's action shapes have them. */
interface AnswerableAction {
  readonly response: unknown;
  readonly response_options: readonly unknown[];
}

/**
 * Whether an action still waits for the operator: it offers answers and none was given yet. An informational message
 * (no response options) is never open, so the inbox neither marks it unread nor explains why it cannot be answered.
 */
export const isOpenAction = (action: AnswerableAction): boolean => action.response_options.length > 0 && action.response === null;

/** Whether any action of the workflow still waits for the operator; see {@link isOpenAction}. */
export const hasOpenAction = (workflow: { readonly workflows_actions: readonly AnswerableAction[] }): boolean => workflow.workflows_actions.some(isOpenAction);

/** The fields of the detail panel's action that it renders as controls; the sidebar list does not carry them. */
interface EditableAction extends AnswerableAction {
  readonly response_input_options: readonly unknown[];
  readonly dropdown_parameters: readonly unknown[];
}

/**
 * Whether an action still takes input from the operator: none was given yet and it offers answers, input fields or
 * dropdowns. Unlike {@link isOpenAction} this includes an action with input fields but no answers: it has nothing to
 * answer, yet its inputs can still be saved, so a caller who may not confirm needs to be told why they are disabled.
 */
export const takesInput = (action: EditableAction): boolean =>
  action.response === null && (action.response_options.length > 0 || action.response_input_options.length > 0 || action.dropdown_parameters.length > 0);

/** Whether any action of the workflow still takes input from the operator; see {@link takesInput}. */
export const hasActionTakingInput = (workflow: { readonly workflows_actions: readonly EditableAction[] }): boolean =>
  workflow.workflows_actions.some(takesInput);
